import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertCircle, Loader2, Plus, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";

export function TeamsIntegrationSetup({ integration }) {
  const API = process.env.REACT_APP_BACKEND_URL + "/api";
  const [activeTab, setActiveTab] = useState("setup");
  const [templates, setTemplates] = useState({ en: {}, ms: {} });
  const [editLang, setEditLang] = useState("en");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState({ tenant_id: "", client_id: "", client_secret: "", organizer_email: "" });
  const [savingCreds, setSavingCreds] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/integrations/teams-config`);
        if (res.data?.email_templates) setTemplates(res.data.email_templates);
        if (res.data?.credentials) {
          setCreds({
            tenant_id: res.data.credentials.tenant_id || "",
            client_id: res.data.credentials.client_id || "",
            client_secret: res.data.credentials.client_secret || "",
            organizer_email: res.data.credentials.organizer_email || "",
          });
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchConfig();
  }, [API]);

  const handleSaveTemplates = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/integrations/teams-config/templates`, { email_templates: templates });
      toast.success("Email templates saved!");
    } catch { toast.error("Failed to save templates"); }
    setSaving(false);
  };

  const handleSaveCreds = async () => {
    setSavingCreds(true);
    try {
      const res = await axios.put(`${API}/integrations/teams-credentials`, { credentials: creds });
      toast.success(`Credentials saved! Status: ${res.data.status}`);
    } catch { toast.error("Failed to save credentials"); }
    setSavingCreds(false);
  };

  const handleTestCreds = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/integrations/teams-credentials/test`);
      setTestResult(res.data);
      if (res.data.success) {
        if (res.data.warning) toast.warning(res.data.message);
        else toast.success(res.data.message);
      } else toast.error(res.data.message);
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.message || "Test connection failed" });
      toast.error("Test connection failed");
    }
    setTesting(false);
  };

  const tpl = templates[editLang] || {};
  const updateField = (field, value) => {
    setTemplates(prev => ({ ...prev, [editLang]: { ...prev[editLang], [field]: value } }));
  };
  const updateFeature = (idx, key, value) => {
    setTemplates(prev => {
      const features = [...(prev[editLang]?.features || [])];
      features[idx] = { ...features[idx], [key]: value };
      return { ...prev, [editLang]: { ...prev[editLang], features } };
    });
  };
  const addFeature = () => {
    setTemplates(prev => {
      const features = [...(prev[editLang]?.features || []), { title: "", desc: "" }];
      return { ...prev, [editLang]: { ...prev[editLang], features } };
    });
  };
  const removeFeature = (idx) => {
    setTemplates(prev => {
      const features = [...(prev[editLang]?.features || [])];
      features.splice(idx, 1);
      return { ...prev, [editLang]: { ...prev[editLang], features } };
    });
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#5B5FC7]" /></div>;

  return (
    <div className="space-y-4" data-testid="teams-setup">
      <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
        {[{id:"setup",label:"Setup Guide"},{id:"credentials",label:"Credentials"},{id:"templates",label:"Email Templates"}].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-colors ${activeTab === t.id ? "bg-white text-[#5B5FC7] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`teams-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "setup" && (
        <div className="space-y-3">
          <div className="p-3 bg-[#5B5FC7]/5 border border-[#5B5FC7]/15 rounded-xl text-xs text-gray-700">
            <p className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-[#5B5FC7]" />
              <span><strong>How it works:</strong> When you schedule a demo from a Lead Detail page, POMEN auto-creates a Teams meeting on your organizer's calendar and emails the invite to the lead, assignee, and creator.</span>
            </p>
          </div>

          {[
            {
              title: "Register an Azure AD app",
              body: (
                <>Go to <a href="https://entra.microsoft.com" target="_blank" rel="noopener noreferrer" className="text-[#5B5FC7] underline font-medium">Microsoft Entra Admin Center</a> → <strong>Applications</strong> → <strong>App registrations</strong> → <strong>+ New registration</strong>. Name it <em>"POMEN Lead Tracker"</em>, choose <strong>Single tenant</strong>, leave Redirect URI blank, then <strong>Register</strong>.</>
              ),
            },
            {
              title: "Copy Tenant ID & Client ID",
              body: (
                <>On the app's <strong>Overview</strong> page, copy <strong>Directory (tenant) ID</strong> and <strong>Application (client) ID</strong>. You'll paste these in the <em>Credentials</em> tab.</>
              ),
            },
            {
              title: "Create a Client Secret",
              body: (
                <><strong>Certificates &amp; secrets</strong> → <strong>+ New client secret</strong> → set expiry to 24 months → click <strong>Add</strong>. Immediately copy the <strong>Value</strong> column (not the Secret ID — Value is shown only once).</>
              ),
            },
            {
              title: "Add API permissions + Grant admin consent",
              body: (
                <>
                  <strong>API permissions</strong> → <strong>+ Add a permission</strong> → <strong>Microsoft Graph</strong> → <strong>Application permissions</strong>. Add these 4 then click <strong>"Grant admin consent"</strong>:
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {["Calendars.ReadWrite", "OnlineMeetings.ReadWrite.All", "User.Read.All", "Mail.Send"].map(p => (
                      <code key={p} className="bg-white border border-gray-200 px-1.5 py-0.5 rounded text-[11px] font-mono">{p}</code>
                    ))}
                  </div>
                  <span className="block mt-1 text-[11px] text-gray-500">All 4 must show green checkmarks before continuing.</span>
                </>
              ),
            },
            {
              title: "Allow the app to access the organizer's calendar",
              body: (
                <>
                  Microsoft requires an Application Access Policy so the app can act on a specific user. Open <strong>PowerShell as Admin</strong> and run:
                  <pre className="mt-1.5 bg-gray-900 text-gray-100 text-[11px] p-2.5 rounded-lg overflow-x-auto leading-relaxed font-mono">{`Install-Module MicrosoftTeams -Force
Connect-MicrosoftTeams

New-CsApplicationAccessPolicy \`
  -Identity "POMEN-Teams-Policy" \`
  -AppIds "<YOUR-CLIENT-ID>" \`
  -Description "POMEN Teams access"

Grant-CsApplicationAccessPolicy \`
  -PolicyName "POMEN-Teams-Policy" \`
  -Identity "taufik@pomen.io"`}</pre>
                  <span className="block mt-1 text-[11px] text-gray-500">Replace <code>&lt;YOUR-CLIENT-ID&gt;</code> with your Client ID, and the email with your organizer. Wait ~30 min for propagation.</span>
                </>
              ),
            },
            {
              title: "Pick a real licensed user as Organizer",
              body: (
                <>
                  The Organizer must be a <strong>licensed M365 user</strong> (Teams + Exchange Online) — not a shared mailbox.<br />
                  <span className="text-emerald-700">✓ Works:</span> <code className="bg-white border px-1 rounded text-[11px]">taufik@pomen.io</code>, <code className="bg-white border px-1 rounded text-[11px]">nazmi@pomen.io</code><br />
                  <span className="text-red-700">✗ Won't work:</span> <code className="bg-white border px-1 rounded text-[11px]">hello@pomen.io</code> (shared inbox), distribution lists, mail aliases.<br />
                  <span className="block mt-1 text-[11px] text-gray-500">Use the user's <strong>User Principal Name (UPN)</strong> from Entra → Users → the user, not a proxy address.</span>
                </>
              ),
            },
            {
              title: "Save credentials & Test Connection",
              body: (
                <>
                  Open the <strong>Credentials</strong> tab → paste Tenant ID, Client ID, Client Secret, and Organizer Email → <strong>Save</strong> → <strong>Test Connection</strong>. A successful test reads: <em>"Connected! Organizer: &lt;name&gt;"</em>.
                </>
              ),
            },
          ].map((step, i) => (
            <div key={i} className="p-3 bg-white border border-gray-200 rounded-xl" data-testid={`teams-setup-step-${i+1}`}>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#5B5FC7] text-white text-xs font-bold flex items-center justify-center">{i+1}</span>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">{step.title}</h4>
                  <div className="text-xs text-gray-600 leading-relaxed">{step.body}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "credentials" && (
        <div className="space-y-4">
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-500">Directory (Tenant) ID *</Label>
              <Input value={creds.tenant_id} onChange={(e) => setCreds({ ...creds, tenant_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="border-gray-300 rounded-lg text-sm font-mono" data-testid="teams-tenant-id" />
              <p className="text-[10px] text-gray-400 mt-0.5">Azure Portal &gt; App Registration &gt; Overview</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Application (Client) ID *</Label>
              <Input value={creds.client_id} onChange={(e) => setCreds({ ...creds, client_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="border-gray-300 rounded-lg text-sm font-mono" data-testid="teams-client-id" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Client Secret *</Label>
              <Input type="password" value={creds.client_secret} onChange={(e) => setCreds({ ...creds, client_secret: e.target.value })} placeholder="Your client secret value" className="border-gray-300 rounded-lg text-sm font-mono" data-testid="teams-client-secret" />
              <p className="text-[10px] text-gray-400 mt-0.5">Certificates & secrets &gt; Client secrets &gt; Value (not Secret ID)</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Organizer Email *</Label>
              <Input value={creds.organizer_email} onChange={(e) => setCreds({ ...creds, organizer_email: e.target.value })} placeholder="hello@pomen.io" className="border-gray-300 rounded-lg text-sm" data-testid="teams-organizer-email" />
              <p className="text-[10px] text-gray-400 mt-0.5">The Microsoft 365 account that will create & host Teams meetings</p>
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
            <h5 className="text-xs font-semibold text-gray-700">Current Status</h5>
            <div className="grid gap-1.5 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Status:</span>
                {integration.status === "connected" ? (
                  <span className="text-emerald-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Connected</span>
                ) : (
                  <span className="text-amber-600 font-medium flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Not configured</span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Organizer:</span>
                <code className="bg-white px-2 py-0.5 rounded border font-mono text-xs">{creds.organizer_email || "Not set"}</code>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleTestCreds} disabled={testing} variant="outline" className="flex-1 border-[#5B5FC7] text-[#5B5FC7] hover:bg-[#5B5FC7]/5 rounded-lg" data-testid="teams-test-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />} Test Connection
            </Button>
            <Button onClick={handleSaveCreds} disabled={savingCreds} className="flex-1 bg-[#5B5FC7] hover:bg-[#4B4FB7] text-white rounded-lg" data-testid="teams-save-creds-btn">
              {savingCreds ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save Credentials
            </Button>
          </div>

          {testResult && (
            <div
              className={`p-3 rounded-lg border text-xs space-y-1.5 ${
                testResult.success && !testResult.warning
                  ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                  : testResult.warning
                  ? "bg-amber-50 border-amber-200 text-amber-900"
                  : "bg-red-50 border-red-200 text-red-900"
              }`}
              data-testid="teams-test-result"
            >
              <div className="flex items-start gap-2">
                {testResult.success && !testResult.warning ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                )}
                <p className="font-medium leading-relaxed">{testResult.message}</p>
              </div>
              {testResult.graph_status && (
                <div className="ml-6 space-y-1 font-mono text-[11px]">
                  <div><span className="opacity-70">HTTP:</span> {testResult.graph_status}</div>
                  {testResult.graph_error_code && (
                    <div><span className="opacity-70">Code:</span> {testResult.graph_error_code}</div>
                  )}
                  {testResult.graph_error_message && (
                    <div className="break-words"><span className="opacity-70">Detail:</span> {testResult.graph_error_message}</div>
                  )}
                </div>
              )}
              {testResult.hint && (
                <div className="ml-6 pt-1.5 border-t border-current/10">
                  <p className="font-sans leading-relaxed"><strong>Hint:</strong> {testResult.hint}</p>
                </div>
              )}
            </div>
          )}

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span><strong>Required Application permissions (with admin consent):</strong> <code className="bg-white px-1 rounded">Calendars.ReadWrite</code>, <code className="bg-white px-1 rounded">OnlineMeetings.ReadWrite.All</code>, <code className="bg-white px-1 rounded">User.Read.All</code>, <code className="bg-white px-1 rounded">Mail.Send</code>. See the <strong>Setup Guide</strong> tab for full steps.</span>
          </div>
        </div>
      )}

      {activeTab === "templates" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Customize the meeting invitation email sent to leads.</p>
            <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
              {[{id:"en",label:"English"},{id:"ms",label:"Bahasa Melayu"}].map(l => (
                <button key={l.id} onClick={() => setEditLang(l.id)} className={`py-1 px-3 text-xs font-medium rounded-md transition-colors ${editLang === l.id ? "bg-white text-[#5B5FC7] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`template-lang-${l.id}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-500">Email Subject</Label>
              <Input value={tpl.subject || ""} onChange={(e) => updateField("subject", e.target.value)} className="border-gray-300 rounded-lg text-sm" placeholder="ENGARAGE Demo Session — {lead_name}" data-testid="tpl-subject" />
              <p className="text-[10px] text-gray-400 mt-0.5">Use {"{lead_name}"} and {"{company}"} as placeholders</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Greeting</Label>
              <Input value={tpl.greeting || ""} onChange={(e) => updateField("greeting", e.target.value)} className="border-gray-300 rounded-lg text-sm" data-testid="tpl-greeting" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Introduction</Label>
              <textarea value={tpl.intro || ""} onChange={(e) => updateField("intro", e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg text-sm p-2.5 resize-none focus:ring-1 focus:ring-[#5B5FC7] focus:border-[#5B5FC7] outline-none" data-testid="tpl-intro" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Features Heading</Label>
              <Input value={tpl.features_heading || ""} onChange={(e) => updateField("features_heading", e.target.value)} className="border-gray-300 rounded-lg text-sm" data-testid="tpl-features-heading" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs text-gray-500">Features List</Label>
                <Button variant="outline" size="sm" onClick={addFeature} className="h-6 text-[10px] border-[#5B5FC7] text-[#5B5FC7]"><Plus className="w-3 h-3 mr-0.5" /> Add</Button>
              </div>
              <div className="space-y-2">
                {(tpl.features || []).map((f, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <div className="flex-1 grid grid-cols-2 gap-1.5">
                      <Input value={f.title} onChange={(e) => updateFeature(i, "title", e.target.value)} placeholder="Feature title" className="border-gray-300 rounded-lg text-xs h-8" />
                      <Input value={f.desc} onChange={(e) => updateFeature(i, "desc", e.target.value)} placeholder="Description" className="border-gray-300 rounded-lg text-xs h-8" />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeFeature(i)} className="h-8 w-8 p-0 text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Closing Message</Label>
              <textarea value={tpl.closing || ""} onChange={(e) => updateField("closing", e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg text-sm p-2.5 resize-none focus:ring-1 focus:ring-[#5B5FC7] focus:border-[#5B5FC7] outline-none" data-testid="tpl-closing" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Sign Off (use new lines to separate)</Label>
              <textarea value={tpl.sign_off || ""} onChange={(e) => updateField("sign_off", e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg text-sm p-2.5 resize-none focus:ring-1 focus:ring-[#5B5FC7] focus:border-[#5B5FC7] outline-none" data-testid="tpl-signoff" />
            </div>
          </div>

          <Button onClick={handleSaveTemplates} disabled={saving} className="w-full bg-[#5B5FC7] hover:bg-[#4B4FB7] text-white rounded-lg" data-testid="save-templates-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save Templates
          </Button>
        </div>
      )}
    </div>
  );
}
